import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import '../ccm/Modal.css';
import { ButtonBlack, ButtonRed, ButtonWhite } from '../reusable';

import { downloadFailuresCsv } from './helpers';

const percent = (processed, total) => (total === 0 ? 0 : Math.floor((processed * 100) / total));

const BulkResults = ({
  submitState,
  progress,
  failures,
  failuresDownloaded,
  inProgressChunk,
  onDownloadFailures,
  onStartOver,
  onRetryFailed,
}) => {
  const [confirmStartOverOpen, setConfirmStartOverOpen] = useState(false);

  if (submitState === 'idle' || submitState === 'confirming') return null;

  const {
    processedCount, successCount, failureCount, total,
  } = progress;
  const running = submitState === 'running';
  const done = submitState === 'done';
  const pct = percent(processedCount, total);

  const downloadAndFlag = () => {
    downloadFailuresCsv(failures);
    onDownloadFailures();
  };

  const requestStartOver = () => {
    if (failures.length > 0 && !failuresDownloaded) {
      setConfirmStartOverOpen(true);
    } else {
      onStartOver();
    }
  };

  const confirmStartOver = () => {
    setConfirmStartOverOpen(false);
    onStartOver();
  };

  return (
    <section className="o2o-bulk-progress">
      <h2 className="headline-3">{running ? 'Running bulk update' : 'Bulk update complete'}</h2>
      <div aria-hidden="true" className="o2o-bulk-progress-bar">
        <div className="o2o-bulk-progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="o2o-bulk-progress-counts">
        <span>{`Processed ${processedCount} of ${total} (${pct}%)`}</span>
        <span className="ok">{`Succeeded: ${successCount}`}</span>
        <span className="err">{`Failed: ${failureCount}`}</span>
      </div>
      {running && (
        <>
          {inProgressChunk && inProgressChunk.length > 0 && (
            <div className="o2o-bulk-in-progress-line">{`In progress: ${inProgressChunk.join(', ')}`}</div>
          )}
          <div className="o2o-bulk-warning mt2-sm">
            <Loading />
            <p>Please do not navigate away from this page while the update runs.</p>
          </div>
        </>
      )}

      {done && (
        <article className="mt4-sm">
          {failures.length === 0 ? (
            <p className="text-color-success body-2">{`All ${successCount} store${successCount === 1 ? '' : 's'} updated successfully.`}</p>
          ) : (
            <>
              <p className="text-color-error body-2">{`${failures.length} store${failures.length === 1 ? '' : 's'} failed:`}</p>
              <div className="o2o-bulk-current-values">
                <table className="o2o-bulk-current-table">
                  <thead>
                    <tr>
                      <th>Store</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failures.map((failure) => (
                      <tr key={failure.storeId}>
                        <td>{failure.storeAlias}</td>
                        <td>{failure.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="o2o-bulk-step2-actions mt4-sm">
            {failures.length > 0 && (
              <ButtonWhite className="border" label="Download Failures CSV" onClick={downloadAndFlag} />
            )}
            {failures.length > 0 && (
              <ButtonBlack label="Retry Failed Stores" onClick={onRetryFailed} />
            )}
            <ButtonBlack className="" label="Start Over" onClick={requestStartOver} />
          </div>
        </article>
      )}

      {confirmStartOverOpen && (
        <div className="modal-overlay">
          <div className="modal-content o2o-bulk-confirm-modal">
            <h2 className="headline-2">Discard unsaved failures?</h2>
            <div className="o2o-bulk-confirm-body">
              <p>{`You have ${failures.length} failure${failures.length === 1 ? '' : 's'} that have not been downloaded. Starting over will discard them.`}</p>
            </div>
            <div className="o2o-bulk-confirm-actions">
              <ButtonWhite className="border" label="Cancel" onClick={() => setConfirmStartOverOpen(false)} />
              <ButtonRed confirm={false} label="Confirm: Discard failures" onClick={confirmStartOver} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

BulkResults.defaultProps = {
  inProgressChunk: [],
};

BulkResults.propTypes = {
  failures: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    storeAlias: PropTypes.string,
    storeId: PropTypes.string,
  })).isRequired,
  failuresDownloaded: PropTypes.bool.isRequired,
  inProgressChunk: PropTypes.arrayOf(PropTypes.string),
  onDownloadFailures: PropTypes.func.isRequired,
  onRetryFailed: PropTypes.func.isRequired,
  onStartOver: PropTypes.func.isRequired,
  progress: PropTypes.shape({
    failureCount: PropTypes.number.isRequired,
    processedCount: PropTypes.number.isRequired,
    successCount: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  submitState: PropTypes.oneOf(['idle', 'confirming', 'running', 'done']).isRequired,
};

export default BulkResults;
